import { Component, OnInit } from '@angular/core';
import { LeftSideNavComponent } from '../../../shared/layout/app/left-menu/left-side-nav.component';
import { VotingBllService } from '../voting-bll.service';
import { VotingEntry } from '../voting.model';
import { CommonModule } from '@angular/common';
import { LoaderService } from '../../../shared/components/loader/loader.service';
import { Router } from '@angular/router';
import { UserEventStatusRespModel } from '../../profile/profile.model';
import { AuthBllService } from '../../auth/auth-bll.service';
import { ProfileBllService } from '../../profile/profile-bll.service';
import { UserService } from '../../../core/services/user.service';
import { VirtualEventBllService } from '../../virtual-event/virtual-event-bll.service';
import { APIErrorModel, BaseResponse } from '../../../shared/models/base-response.model';
import { DeviceTypeService } from '../../../core/services/device-type.service';
import { HomeBllService } from '../../home/home-bll.service';
import { EventDetailsResponseModel } from '../../home/home.model';

@Component({
  selector: 'app-participants-list',
  imports: [LeftSideNavComponent, CommonModule],
  templateUrl: './participants-list.component.html',
  styleUrl: './participants-list.component.scss'
})
export class ParticipantsListComponent implements OnInit {

  /**
  * User profile data
  */
  userProfile: UserEventStatusRespModel["user_profile"] | null = null;
  hasRegisteredAsVirtualVisitor: boolean = false;

  // User authentication status
  isAuthenticated: boolean = false;
  
  participantsList: VotingEntry[] = []; // List of voting participants
  userContestVoteCount: number = 0; // Number of votes cast by the user
  maxAllowedVoteCount: number = 3; // Maximum votes allowed per user
  votedParticipantIds: number[] = []; // IDs of participants the user has voted for

  isLoading: boolean = true; 
  isContestNotActive: boolean = false; // Indicates if contest is currently not active
  isVotingActive : boolean = true;

  // variable to hold the source of user(android_web/ios_web/web)
  source: string = "";

  contestId!: number;
  eventDetails: EventDetailsResponseModel | null = null;

  constructor(
    private votingBllService: VotingBllService,
    private loaderService: LoaderService,
    private router: Router,
    private authBllService: AuthBllService,
    private profileBll: ProfileBllService,
    private userService: UserService,
    private virtualBll: VirtualEventBllService,
    private deviceService: DeviceTypeService,
    private homeBll: HomeBllService
  ) { }

  ngOnInit(): void {
    this.callFetchParticipantsList();

    // Get the source of user
    this.source = this.deviceService.getUserSource();
      
    this.authBllService.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });

    if (this.isAuthenticated) {
      this.fetchUserEventStatus();
      this.callFetchVoteStatus();
    }
    this.loadEventDetails();
  }

  /**
     * @description Fetches the user's event status and updates the user profile and service accordingly.
     */
  fetchUserEventStatus() {
    this.profileBll
      .submitGetUserEventStatus()
      .then((res: BaseResponse<UserEventStatusRespModel>) => {
        this.userProfile = res.data.user_profile;
        if (this.userProfile) {
          const previewLogo =
            this.userProfile.image_url ??
            "/assets/images/Default-profile-photo.png";
          const userName = `${this.userProfile.first_name} ${this.userProfile.last_name}`;
          this.userService.setUser({
            name: userName,
            profileImg: previewLogo,
            mobile: this.userProfile.mobile,
            state: this.userProfile.state,
            city: this.userProfile.city,
            hasUnassignedTickets:
              res.data.event_my_tickets.unassigned_tickets_bought_by_me > 0,
            hasAssignedTickets:
              res.data.event_my_tickets.assigned_tickets_bought_by_me > 0 ||
              res.data.event_my_tickets.tickets_assigned_for_me > 0

          });
        }

        /**
         * @description Check if the user is an event attendee and register as a virtual visitor if true.
         */
        const attendee = res?.data?.event_my_tickets?.event_attendee_details;
        if (!attendee) {
          this.registerVirtualVisitor();
        }

      })
      .catch((err: APIErrorModel) => {
        console.error("Error fetching user event status:", err);
      });
  }

  /**
   * @description Fetches the voting status of the user.
   */
  callFetchVoteStatus() {
    this.votingBllService.fetchVoteStatus()
      .then((response) => {
        this.userContestVoteCount = response.data.user_contest_vote_count;
        this.votedParticipantIds = response.data.voted_participant_ids_by_user;
      })
      .catch((error) => {
        console.error("Error fetching vote status:", error);
      });
  }

  /**
   * @description Fetches the list of contest participants.
   */
  callFetchParticipantsList(): void {
    this.loaderService.show();
    this.isLoading = true;
    this.votingBllService.fetchParticipantsList()
      .then(response => {
        this.participantsList = response.data?.entries || [];
        this.maxAllowedVoteCount = response.data?.max_vote_per_user || 0;
        this.isVotingActive =  response.data?.is_voting_active ?? true;
        
        this.loaderService.hide();
        this.isLoading = false;
        this.isContestNotActive = false;

        this.contestId = response.data?.contest_id;
        if(this.isAuthenticated){
          this.callSubmitContestActivityLog('view_contest', this.source);
        }
      })
      .catch(error => {   
        this.loaderService.hide();
        this.isLoading = false; 
        
        if(error.status === 410 ){
          this.isContestNotActive = true;
          this.router.navigate(['home']);
        }

      });
  }

  /**
   * @param participant The participant whose video details are to be opened.
   */
  openVideoDetails(participant: VotingEntry): void {
    this.votingBllService.saveVideoDetails({
      id: participant.id,
      encodedId: participant.encoded_participant_id,
      primary_media_url: participant.primary_media_url,
      download_media_url: participant.download_media_url,
      title: participant.title,
      is_voting_active: this.isVotingActive
    });
    this.router.navigate(['contests/details', participant.encoded_participant_id]);
  }

  /**
   * @description Checks if a participant has been voted for by the user.
   * @param participantId The ID of the participant to check.
   * @returns True if the participant has been voted for by the user, false otherwise.
   */
  isParticipantVoted(participantId: number): boolean {
    return this.votedParticipantIds.includes(participantId);
  }
  
  /**
   * @description Registers the user as a virtual visitor for the event.
   */
  registerVirtualVisitor() {
    this.virtualBll.submitRegisterVirtualVisitor().then(() => {
      // Registration successful
      this.hasRegisteredAsVirtualVisitor = true;
    }).catch((error) => {
      this.hasRegisteredAsVirtualVisitor = false;
    });
  }

  /**
   * @description Navigates the user back to the home page.
   */
  goBack(){
    this.router.navigate(['home']);
  }

  callSubmitContestActivityLog(activityCode: string, source: string) {
    const payload = {
      activity_code: activityCode,
      activity_for_item_id: this.contestId,
      source: source
    }

     this.votingBllService.submitContestActivityLog(payload)
      .then((res) => {
        // No further action needed as we just log the activity
      }).catch((err) => {
        console.log("Error submitting contest activity log:", err);
      });
  }

  loadEventDetails() {
    this.homeBll.fetchEventDetails()
      .then((response) => {
        this.eventDetails = response.data;     
        this.userService.setLeftMenu({
          allow_to_register: this.eventDetails.allow_to_register,
          show_digital_catalog: this.eventDetails.show_digital_catalog,
          event_status: this.eventDetails.event_status
        });       
      })
      .catch((error) => {
        console.error('Error fetching event details:', error);
      });
  }

}
